import { createTheme, responsiveFontSizes } from "@mui/material";

/**
 * Application theme — the single source of truth for design tokens.
 *
 * `cssVariables: { colorSchemeSelector: "class" }` (MUI v6) makes `createTheme` emit
 * `--mui-palette-*` (and typography/spacing) CSS custom properties AND a light + dark
 * variant of every palette var, toggled by a `.light` / `.dark` class on `<html>`. The
 * ~16 component CSS files consume those generated vars directly, so most surfaces switch
 * schemes for free (this is what retired the hand-maintained `tokens.css`; design-audit §3, §8).
 *
 * Dark mode (customer-portal-qdc5): the user's Light/Dark/System choice is driven entirely
 * by MUI's own `useColorScheme()` hook (localStorage key `mui-mode`, matchMedia system-
 * following). MUI v6.5 merged `CssVarsProvider` into the plain `ThemeProvider`, so the single
 * `<ThemeProvider theme={theme}>` in AppProviders is all that's needed — do NOT add a
 * `CssVarsProvider` (a v5-era reflex). Flash-of-wrong-theme on first paint is prevented by the
 * static `InitColorSchemeScript` markup in `Web/index.html` (a mounted React component can't
 * pre-empt first paint) — its `attribute="class"` must stay in sync with `colorSchemeSelector`.
 *
 * `colorSchemes.light.palette` is a VERBATIM copy of the pre-dark-mode palette, so light mode
 * is visually unchanged. The semantic slots (success/warning/error/info) match the hexes
 * already in use (design-audit §2.2/§2.4). `colorSchemes.dark.palette` is a DESIGNED dark
 * palette (not a naive inversion): only the values that must change per scheme are overridden;
 * text/divider/action inherit MUI's dark defaults.
 *
 * NOTE: only `palette` (and `shadows`) may vary per scheme — `typography`, `spacing`, `shape`,
 * and `components` stay top-level.
 *
 * This module is intentionally free of app/provider imports so it can be unit-tested in
 * isolation (AppProviders.tsx pulls the whole component graph, incl. the Toolbar deep-icon
 * import that poisons the vitest transform).
 */

// Brand colors are identical across schemes (navy AppBar reads consistently in both) — kept as
// consts so "same value in both schemes" is structural, not just a prose comment.
const BRAND_PRIMARY = "#274052";
const BRAND_SECONDARY = "#dc004e";

const baseTheme = createTheme({
	cssVariables: { colorSchemeSelector: "class" },
	colorSchemes: {
		light: {
			palette: {
				primary: {
					main: BRAND_PRIMARY,
				},
				secondary: {
					main: BRAND_SECONDARY,
				},
				// Semantic slots — values match colors already in use (design-audit §2.4).
				error: {
					main: "#d32f2f", // == former --color-error / MUI default error.main
					dark: "#b71c1c", // == ERROR_STROKE (OpsMap.layers)
				},
				warning: {
					main: "#ed6c02", // "// warning.main" annotation in DealCard
					light: "#ff9800", // age-status / map warning orange
				},
				success: {
					main: "#4caf50", // #4caf50 (12 occ) → success.main per §2.4
					dark: "#2e7d32", // "// success.dark" annotation in DealCard
					light: "#66bb6a",
				},
				info: {
					main: "#1976d2", // UI "current/active" blue accent (RobotList, §2.2)
				},
			},
		},
		dark: {
			palette: {
				// The dark scheme needs a LIGHTER primary: brand navy (#274052) is near-invisible
				// as outlined/text buttons, chips, and links on dark surfaces (verification pass
				// found exactly that across the wizard and Job Overview). Same hue family, lifted
				// for contrast. The AppBar is unaffected: MUI's AppBar uses its dark paper
				// background in dark mode (enableColorOnDark defaults to false), and in light mode
				// primary.main is still brand navy. Brand SURFACES that hardcode white text (the
				// Home cards) pin navy directly instead of using this slot.
				primary: {
					main: "#7fa8c9",
					// Explicit hover shade: MUI's auto-derived dark (#58758C) drops contained-
					// button hover text to 3.97:1 with the auto contrastText (adversarial-review
					// finding). #5d87ab keeps >=5.5:1 with the dark contrastText.
					dark: "#5d87ab",
				},
				secondary: {
					// Lifted for dark: brand crimson (#dc004e) measures 3.33:1 as text on dark
					// rows (dashboard group headers read as an error state). Same-hue pink-200.
					main: "#f48fb1",
				},
				// Semantic mains carry over; MUI's dark scheme derives readable light/dark
				// shades. Overrides added only where a contrast check fails.
				error: {
					main: "#f44336", // MUI dark-scheme error.main (brighter for dark bg)
					dark: "#d32f2f",
				},
				warning: {
					main: "#ffa726", // MUI dark-scheme warning.main
				},
				success: {
					main: "#66bb6a", // MUI dark-scheme success.main (lighter for dark bg)
				},
				info: {
					main: "#42a5f5", // MUI dark-scheme info.main
				},
				// Disabled emphasis, slightly raised from MUI's dark defaults (0.3/0.12):
				// several pages' PRIMARY call-to-action starts disabled (wizard NEXT, App Logs
				// downloads, Generate Password) and measured 2.5:1 — ghosts users can't see.
				action: {
					disabled: "rgba(255, 255, 255, 0.45)",
					disabledBackground: "rgba(255, 255, 255, 0.16)",
				},
				// Designed dark surfaces: paper slightly elevated above the base so cards /
				// dialogs / the drawer read as distinct layers. text/divider inherit MUI dark
				// defaults (rgba-white ramps), which the chrome CSS consumes via vars.
				background: {
					default: "#121212",
					paper: "#1e1e1e",
				},
			},
		},
	},
	// Typography — anchor the family/weights so a scale exists to lean on. Variant
	// sizes stay at MUI defaults (appearance-preserving); responsiveFontSizes below
	// folds in the mobile-heading scaling the app currently fakes with inline ternaries.
	typography: {
		fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
		fontWeightRegular: 400,
		fontWeightMedium: 500,
		fontWeightBold: 700,
	},
	// Spacing — MUI's default 8px scale; the app already uses it implicitly via `sx`
	// numbers (design-audit §4). Stated explicitly to make it a deliberate decision.
	spacing: 8,
	// Shape — 4px is MUI's default and the app's most common rendered radius
	// (buttons/inputs/selects; design-audit §6). Held constant to avoid a restyle.
	shape: {
		borderRadius: 4,
	},
	components: {
		// The app is a pure client-rendered SPA with 36+ useMediaQuery call sites
		// (design-audit §3). noSsr skips the first-paint pass where every query
		// resolves false, removing a layout flash. Final render is identical.
		MuiUseMediaQuery: {
			defaultProps: { noSsr: true },
		},
		// Real keyboard-focus ring. MUI's default focusVisible cue is a ~12%-alpha overlay,
		// which is indistinguishable on dark surfaces (WCAG 2.4.7 finding from the dark-mode
		// adversarial review). Keyboard-only (Mui-focusVisible), so pointer clicks are
		// unaffected; primary var resolves per scheme.
		MuiButtonBase: {
			styleOverrides: {
				root: {
					"&.Mui-focusVisible": {
						outline: "2px solid var(--mui-palette-primary-main)",
						outlineOffset: "2px",
					},
				},
			},
		},
		// Badge numerals: white 12px on error red #f44336 measures 3.68:1. Darken the dark-
		// scheme badge fill to keep white text >=6:1; light mode unchanged.
		MuiBadge: {
			styleOverrides: {
				colorError: ({ theme: t }) => ({
					...t.applyStyles("dark", { backgroundColor: "#b71c1c" }),
				}),
			},
		},
		// Outlined is the app-wide text-field convention and MUI's default — anchored
		// here so it is a decision, not an accident (design-audit §6.1). No-op today.
		MuiTextField: {
			defaultProps: { variant: "outlined" },
		},
		// Status chips are used at size="small" across the app (JobOverviewCard,
		// flag chips, DealPropertyChip; design-audit §3). Making it the default lets
		// those call sites drop the repeated prop over time.
		MuiChip: {
			defaultProps: { size: "small" },
		},
	},
});

export const theme = responsiveFontSizes(baseTheme);
